import React, { useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { formatCurrencyTotals } from "../lib/money";
import { ExternalLink, Users, DollarSign, FileText, TrendingUp, RefreshCw, Check } from "lucide-react";
import { toast } from "sonner";

const ICONS = {
  onlyfans_vip: "OF+",
  onlyfans_free: "OF",
  justforfans: "JFF",
  rawfuckclub: "RFC",
  twitter: "X",
  instagram: "IG",
  tiktok: "TT",
  bluesky: "BSKY",
  linktree: "LT",
};

export default function Stats({ extraTabBar }) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    const [l, s] = await Promise.all([api.get("/api/platform-stats/"), api.get("/api/platform-stats/summary")]);
    setItems(l.data); setSummary(s.data);
  };

  const seed = async () => {
    try {
      const r = await api.post("/api/platform-stats/seed");
      toast.success(`Seeded ${r.data.added} platforms (total ${r.data.total})`);
      load();
    } catch (e) {
      // Seed endpoints are disabled unless the server sets ALLOW_SEED=1.
      toast.error(e?.response?.data?.detail || "Seed failed");
    }
  };

  useEffect(() => {
    load().then(async () => {
      // Auto-seed if empty
      const r = await api.get("/api/platform-stats/");
      if (r.data.length === 0) {
        try {
          await api.post("/api/platform-stats/seed");
          load();
        } catch { /* seeding disabled (ALLOW_SEED unset) — leave the list empty */ }
      }
    });
    // eslint-disable-next-line
  }, []);

  const updateField = async (id, field, value) => {
    setSavingId(id);
    try {
      const val = ["followers", "subscribers", "posts_count"].includes(field) ? parseInt(value || 0) : parseFloat(value || 0);
      await api.put(`/api/platform-stats/${id}`, { [field]: isNaN(val) ? 0 : val });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingId(null); }
  };

  const updateText = async (id, field, value) => {
    setSavingId(id);
    try {
      await api.put(`/api/platform-stats/${id}`, { [field]: value });
      await load();
    } catch (e) { toast.error("Save failed"); }
    finally { setSavingId(null); }
  };

  return (
    <AppLayout title="Platform stats" overline="140 · ALL CHANNELS · MANUAL UPDATES">
      {extraTabBar}
      {/* Top summary cards */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-10 fade-up" data-testid="stats-summary">
        <Summary label="Total followers" value={(summary?.total_followers ?? 0).toLocaleString()} icon={<Users size={16} />} testid="sum-followers" big />
        <Summary label="Paid subscribers" value={(summary?.total_subscribers ?? 0).toLocaleString()} icon={<Users size={16} />} testid="sum-subscribers" />
        <Summary label="Total posts" value={(summary?.total_posts ?? 0).toLocaleString()} icon={<FileText size={16} />} testid="sum-posts" />
        <Summary label="Platform revenue" value={summary?.total_revenue_by_currency ? formatCurrencyTotals(summary.total_revenue_by_currency, { decimals: 0 }) : `$${(summary?.total_revenue ?? 0).toFixed(0)}`} icon={<DollarSign size={16} />} testid="sum-revenue" />
      </section>

      <div className="flex items-center justify-between mb-4">
        <div className="overline">{items.length} CONNECTED PLATFORMS · {summary?.paid_count ?? 0} PAID</div>
        <button onClick={seed} data-testid="stats-reseed-button" className="btn-ghost inline-flex items-center gap-2">
          <RefreshCw size={14} /> Add missing platforms
        </button>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="stats-grid">
        {items.map((p) => (
          <div key={p.id} className={`border border-zinc-900 bg-zinc-950 p-5 transition hover:border-[color:var(--gio-primary)]/40 ${p.is_paid ? "shadow-[inset_3px_0_0_0_var(--gio-primary)]" : ""}`} data-testid={`stat-${p.platform}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 border border-zinc-800 flex items-center justify-center font-heading text-lg text-[color:var(--gio-primary)]">
                  {ICONS[p.platform] || p.platform.slice(0, 3).toUpperCase()}
                </div>
                <div>
                  <div className="font-heading text-xl text-white leading-tight">{p.display_name}</div>
                  <div className="overline mt-1">@{p.handle} {p.is_paid && <span className="text-[color:var(--gio-primary)] ml-1">· PAID</span>}</div>
                </div>
              </div>
              <a href={p.url} target="_blank" rel="noreferrer" data-testid={`stat-link-${p.platform}`} className="text-zinc-500 hover:text-[color:var(--gio-primary)]">
                <ExternalLink size={16} />
              </a>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <StatField label="Followers" value={p.followers} onSave={(v) => updateField(p.id, "followers", v)} testid={`stat-${p.platform}-followers`} />
              {p.is_paid && (
                <StatField label="Subscribers" value={p.subscribers} onSave={(v) => updateField(p.id, "subscribers", v)} testid={`stat-${p.platform}-subs`} />
              )}
              <StatField label="Posts" value={p.posts_count} onSave={(v) => updateField(p.id, "posts_count", v)} testid={`stat-${p.platform}-posts`} />
              {p.is_paid && (
                <StatField label="Revenue $" value={p.total_revenue} onSave={(v) => updateField(p.id, "total_revenue", v)} testid={`stat-${p.platform}-revenue`} decimal />
              )}
              {!p.is_paid && (
                <StatField label="Engagement %" value={p.engagement_rate} onSave={(v) => updateField(p.id, "engagement_rate", v)} testid={`stat-${p.platform}-eng`} decimal />
              )}
            </div>

            <div className="mt-3">
              <textarea
                defaultValue={p.notes || ""}
                onBlur={(e) => e.target.value !== (p.notes || "") && updateText(p.id, "notes", e.target.value)}
                rows={2}
                placeholder="Notes..."
                data-testid={`stat-${p.platform}-notes`}
                className="w-full bg-black border border-zinc-900 text-zinc-300 p-2 text-xs focus:outline-none focus:border-[color:var(--gio-primary)]"
              />
            </div>

            <div className="flex items-center justify-between mt-3">
              <div className="overline text-zinc-700">UPDATED {p.last_updated?.slice(0, 10)}</div>
              {savingId === p.id && <div className="overline text-[color:var(--gio-primary)] flex items-center gap-1"><Check size={10} />SAVED</div>}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 border border-zinc-900 bg-zinc-950 p-5 text-zinc-400 text-sm" data-testid="stats-explainer">
        <div className="flex items-center gap-2 overline text-[color:var(--gio-primary)] mb-3"><TrendingUp size={12} /> WHY MANUAL?</div>
        Adult platforms (OnlyFans, JustForFans, RawFuckClub) don't expose public APIs. Update these numbers weekly (or paste from your Fedica export for X) — the Revenue tracker auto-calculates $$. Tip: share the link in bio (linktr.ee/giopilos) to funnel free-platform followers into paid subs.
      </div>
    </AppLayout>
  );
}

function StatField({ label, value, onSave, testid, decimal }) {
  const [local, setLocal] = useState(value ?? 0);
  useEffect(() => { setLocal(value ?? 0); }, [value]);
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      <input
        type="number"
        step={decimal ? "0.01" : "1"}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => String(local) !== String(value) && onSave(local)}
        data-testid={testid}
        className="w-full bg-black border border-zinc-900 text-white p-2 text-sm focus:outline-none focus:border-[color:var(--gio-primary)] font-mono-ui"
      />
    </div>
  );
}

function Summary({ label, value, icon, testid, big }) {
  return (
    <div className="border border-zinc-900 bg-zinc-950 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 overline mb-3">{icon}{label}</div>
      <div className={`font-heading text-white leading-none ${big ? "text-5xl" : "text-4xl"}`}>{value}</div>
    </div>
  );
}
